import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getMetaKeywords, SEO_TITLES, SEO_DESCRIPTIONS } from '../utils/seoKeywords';

const HERO_VIDEO_SRC = '/assets/hero-background.mp4';
const HERO_POSTER_SRC = '/assets/hero-poster.webp';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "name": "At Every Desk - Paper Bottle India | 100% Compostable Paper Bottle",
        "alternateName": "AED",
        "url": "https://www.ateverydesk.com",
        "description": "Global manufacturer of 100% compostable paper bottles and packaging solutions. Sustainable, compostable packaging including paper bottles, bags, preforms, caps, and food packaging. Available worldwide.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://www.ateverydesk.com/?s={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is a paper bottle and how is it different from plastic bottles?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A paper bottle is a 100% compostable alternative to traditional plastic bottles. Made from renewable bamboo and sugar cane fibers, paper bottles decompose naturally without leaving harmful microplastics. At Every Desk manufactures paper bottles with extended shelf life up to 6 months, making them suitable for beverages, liquids, and food products. Unlike plastic bottles, paper bottles are fully compostable and contribute to zero carbon footprints."
            }
          },
          {
            "@type": "Question",
            "name": "Where can I buy compostable paper bottles?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "At Every Desk (AED) is a global manufacturer and supplier of compostable paper bottles. We offer paper bottles in three refined sizes — 500ml, 750ml and 1L — for wholesale, retail, and custom branding. Contact us at info@ateverydesk.com or +91 9377004949 for domestic enquiries, or info@gatewaysglobal.co.uk / +44 7909494684 for international enquiries. We ship worldwide."
            }
          },
          {
            "@type": "Question",
            "name": "Are paper bottles safe for food and beverages?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, At Every Desk paper bottles are 100% food-safe and certified by FSSAI (Food Safety and Standards Authority of India), CIPET (Central Institute of Petrochemicals Engineering & Technology), and CPCB (Central Pollution Control Board). Our paper bottles are made from natural fibers and are suitable for water, juice, beverages, and food products with extended shelf life up to 6 months."
            }
          },
          {
            "@type": "Question",
            "name": "How long do paper bottles last before they expire?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "At Every Desk paper bottles have an extended shelf life of up to 6 months, making them suitable for commercial use. The bottles maintain their structural integrity and protect contents while being 100% compostable at end of life."
            }
          },
          {
            "@type": "Question",
            "name": "What makes At Every Desk paper bottles compostable?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "At Every Desk paper bottles are made from 100% renewable natural fibers including bamboo and sugar cane pulp. The entire bottle, including the cap and closure, is fully compostable. Our patented design and process ensure the bottles decompose naturally without leaving microplastics, contributing to zero carbon footprints and environmental sustainability."
            }
          },
          {
            "@type": "Question",
            "name": "Can I get custom branded paper bottles?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, At Every Desk offers custom branding, printing, and OEM services for paper bottles. We can customize bottle sizes, designs, colors, and branding to meet your specific requirements. Contact us for private label, custom paper bottle solutions, and bulk orders."
            }
          },
          {
            "@type": "Question",
            "name": "What other compostable products does At Every Desk offer?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "At Every Desk offers a complete range of compostable packaging solutions including paper bottles, compostable bags (carry bags, grocery bags, shopping bags, biomedical waste bags, garbage bags, apparel bags, plant grow bags), preforms, caps, food packaging, and stretch film. All products are 100% compostable."
            }
          },
          {
            "@type": "Question",
            "name": "Is the paper bottle design patented?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, At Every Desk paper bottle design and manufacturing process are protected by patent. Our innovative technology ensures both performance and compostability."
            }
          },
          {
            "@type": "Question",
            "name": "Who is a leading sustainable bottle manufacturer in India?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "At Every Desk (AED) is a leading sustainable bottle manufacturer in India, offering 100% compostable paper bottles and compostable beverage packaging. We are CIPET, CPCB and FSSAI certified and supply brands and distributors worldwide."
            }
          },
          {
            "@type": "Question",
            "name": "Where to find compostable beverage packaging?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "At Every Desk supplies compostable beverage packaging including paper bottles in 500ml, 750ml and 1L. Contact us at info@ateverydesk.com or +91 9377004949 for India and +44 7909494684 for international enquiries."
            }
          }
        ]
      },
      {
        "@type": "WebPage",
        "name": "Paper Bottle AED | 100% Compostable Paper Bottle Manufacturer | At Every Desk",
        "description": "Paper bottle AED - Global manufacturer of 100% compostable paper bottles. Sustainable, compostable packaging solutions with 6-month shelf life. Available worldwide.",
        "url": "https://www.ateverydesk.com/",
        "inLanguage": "en-IN",
        "isPartOf": {
          "@type": "WebSite",
          "name": "At Every Desk",
          "url": "https://www.ateverydesk.com"
        }
      },
      {
        "@type": "Product",
        "name": "Paper Bottle - 100% Compostable Paper Bottle",
        "description": "100% compostable paper bottle with extended shelf life up to 6 months. Made from renewable bamboo and sugar cane fibers. 100% compostable including cap and closure. Available globally.",
        "image": "https://www.ateverydesk.com/assets/AED-PRODUCT.png",
        "brand": {
          "@type": "Brand",
          "name": "At Every Desk",
          "logo": "https://www.ateverydesk.com/assets/Logo.png"
        },
        "category": "Sustainable Packaging",
        "material": ["Bamboo Fiber", "Sugar Cane Pulp"],
        "countryOfOrigin": {
          "@type": "Country",
          "name": "India"
        },
        "certification": [
          {
            "@type": "Certification",
            "name": "CIPET Certified"
          },
          {
            "@type": "Certification",
            "name": "CPCB Standards"
          },
          {
            "@type": "Certification",
            "name": "FSSAI Certified"
          }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "INR"
        }
      },
      {
        "@type": "Organization",
        "name": "At Every Desk",
        "alternateName": "AED",
        "url": "https://www.ateverydesk.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.ateverydesk.com/assets/Logo.png",
          "width": 512,
          "height": 512
        },
        "image": "https://www.ateverydesk.com/assets/Logo.png",
        "description": "Global manufacturer of 100% compostable paper bottles and packaging solutions. Sustainable, compostable packaging including paper bottles, bags, preforms, caps, and food packaging. Available worldwide.",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-9377004949",
          "contactType": "Customer Service",
          "email": "info@ateverydesk.com",
          "availableLanguage": "English"
        },
        "areaServed": [
          {
            "@type": "Country",
            "name": "India"
          },
          {
            "@type": "Place",
            "name": "Worldwide"
          }
        ],
        "sameAs": [
          "https://www.instagram.com/at_every_desk/",
          "https://www.linkedin.com/company/at-every-desk/"
        ]
      }
    ]
  };

  return (
    <>
      <SEO
        title={SEO_TITLES.home}
        description={SEO_DESCRIPTIONS.home}
        keywords={getMetaKeywords('home')}
        geoRegion="IN"
        geoPlacename="India"
        structuredData={homeStructuredData}
      />
      <main id="main-content" role="main" tabIndex="-1">
        <section className="hero" id="hero">
        <div className="hero__video-background">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="hero__video"
            preload="metadata"
            poster={HERO_POSTER_SRC}
            suppressHydrationWarning
            onCanPlay={(e) => {
              e.target.play().catch(() => {});
            }}
          >
            <source src={HERO_VIDEO_SRC} type="video/mp4" />
          </video>
          <div className="hero__video-overlay"></div>
          <div className="hero__animated-fallback"></div>
        </div>
        <div className="hero__grid container">
          <div className="hero__content animate">
            <h1 className="display">Paper Bottle Manufacturer — 100% Compostable Paper Bottle India</h1>
            <p className="hero__tagline">Hold the future lightly</p>
            <p className="subhead"><strong>Paper Bottle India</strong> — <strong>100% compostable paper bottle</strong> manufacturing for a step towards <strong>0 carbon footprints</strong> and <strong>Swachh Bharat</strong>. From paper bottles to compostable bags, preforms, caps and food packaging, all with extended shelf life <strong>up to 6 months</strong>.</p>
            <p className="meta">Made in India</p>
            <p className="subhead" style={{ marginTop: '0.75rem', fontSize: '0.95em', opacity: 0.95 }}>Leading sustainable bottle manufacturer in India and trusted source for compostable beverage packaging.</p>
            <div className="cta-row">
              <Link to="/product" className="btn">
                <span>Explore Paper Bottle</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/other-products" className="btn btn--ghost">
                <span>View All Products</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </Link>
            </div>
            <ul className="trust row">
              <li>CIPET certified</li>
              <li>FSSAI certified</li>
              <li>CPCB standards aligned</li>
              <li>100% compostable</li>
            </ul>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default Home;

